import React, { useContext, useEffect, useState } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { OpenAgentsContext } from "@/context/OpenAgentsProvider";
import { useInterviewPortalStore } from "@/stores/interviewPortalStore";
import { useAuthStore } from "@/stores/authStore";
import { isInterviewHubMode } from "@/config/interviewHubConfig";
import JobListView from "./JobListView";
import JobDetailView from "./JobDetailView";
import InterviewerHubView from "./InterviewerHubView";
import RightSidebar from "./RightSidebar";
import UserRegistrationModal from "./UserRegistrationModal";

const INTERVIEW_DESTINATION = "mod:openagents.mods.workspace.interview";

const InterviewMainPage: React.FC = () => {
  const context = useContext(OpenAgentsContext);
  const connection = context?.connector || null;
  const isConnected = context?.isConnected;
  const navigate = useNavigate();

  const {
    setConnection,
    userInfo,
    userInfoChecked,
    checkUserInfo,
    registerUser,
    loadJobs,
    loadNotifications,
    loadInterviews,
    fetchAssessmentStatus,
  } = useInterviewPortalStore();

  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [autoRegistrationAttempted, setAutoRegistrationAttempted] = useState(false);
  const [isVerifyingResume, setIsVerifyingResume] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);

  useEffect(() => {
    if (connection) {
      setConnection(connection);
      return () => {
        setConnection(null);
      };
    }
  }, [connection, setConnection]);

  // Check user info on connection
  useEffect(() => {
    if (!connection || !isConnected) {
      return;
    }
    checkUserInfo();
  }, [connection, isConnected, checkUserInfo]);

  // Auto-register in Interview Hub mode with resume verification
  useEffect(() => {
    if (
      isInterviewHubMode() &&
      connection &&
      isConnected &&
      userInfoChecked &&
      !userInfo &&
      !autoRegistrationAttempted &&
      !isVerifyingResume
    ) {
      // Try to get stored user info from localStorage
      const storedUserInfoStr = localStorage.getItem('interview_hub_user_info');
      if (storedUserInfoStr) {
        try {
          const storedUserInfo = JSON.parse(storedUserInfoStr);
          console.log('🎯 Interview Hub Mode: Starting verification and registration', storedUserInfo);

          // Check if resume data exists
          if (storedUserInfo.resume_base64 && storedUserInfo.resume_filename) {
            setIsVerifyingResume(true);
            setVerificationError(null);

            // Async function to handle verification flow
            const verifyAndRegister = async () => {
              try {
                // Step 1: Upload the PDF
                console.log('📤 Uploading resume...');
                const uploadResponse = await connection.sendEvent({
                  event_name: "interview.file.upload",
                  destination_id: INTERVIEW_DESTINATION,
                  payload: {
                    filename: storedUserInfo.resume_filename,
                    file_content: storedUserInfo.resume_base64,
                    mime_type: "application/pdf",
                    file_size: storedUserInfo.resume_size || 0,
                  },
                });

                if (!uploadResponse.success || !uploadResponse.data?.file_id) {
                  throw new Error(uploadResponse.message || "Failed to upload resume");
                }

                const fileId = uploadResponse.data.file_id;
                console.log('✅ Resume uploaded, file_id:', fileId);

                // Step 2: Verify the resume
                console.log('🔍 Verifying resume...');
                const verifyResponse = await connection.sendEvent({
                  event_name: "interview.resume.verify",
                  destination_id: INTERVIEW_DESTINATION,
                  payload: {
                    file_id: fileId,
                    first_name: storedUserInfo.first_name,
                    last_name: storedUserInfo.last_name,
                  },
                });

                if (!verifyResponse.success) {
                  // Verification failed
                  console.error('❌ Resume verification failed:', verifyResponse.message);
                  setVerificationError(verifyResponse.message || "Resume verification failed");
                  setIsVerifyingResume(false);
                  setAutoRegistrationAttempted(true);
                  // Clear stored data so user can retry
                  localStorage.removeItem('interview_hub_user_info');
                  return;
                }

                console.log('✅ Resume verified successfully');

                // Step 3: Register user with resume file_id
                console.log('📝 Registering user...');
                const registrationData = {
                  email: storedUserInfo.email,
                  first_name: storedUserInfo.first_name,
                  last_name: storedUserInfo.last_name,
                  resume_file_id: fileId,
                };

                const success = await registerUser(registrationData);

                if (success) {
                  console.log('✅ User registered successfully');
                  localStorage.removeItem('interview_hub_user_info');
                } else {
                  setVerificationError("Failed to complete registration. Please try again.");
                }

                setIsVerifyingResume(false);
                setAutoRegistrationAttempted(true);
              } catch (error: any) {
                console.error('❌ Verification/registration error:', error);
                setVerificationError(error.message || "An error occurred during verification");
                setIsVerifyingResume(false);
                setAutoRegistrationAttempted(true);
                localStorage.removeItem('interview_hub_user_info');
              }
            };

            verifyAndRegister();
          } else {
            // No resume data, use legacy registration flow
            console.log('⚠️ No resume data found, using legacy registration');
            setAutoRegistrationAttempted(true);
            registerUser({
              email: storedUserInfo.email,
              first_name: storedUserInfo.first_name,
              last_name: storedUserInfo.last_name,
            }).then((success) => {
              if (success) {
                localStorage.removeItem('interview_hub_user_info');
              }
            });
          }
        } catch (error) {
          console.error('Failed to parse stored user info:', error);
          setAutoRegistrationAttempted(true);
        }
      } else {
        setAutoRegistrationAttempted(true);
      }
    }
  }, [connection, isConnected, userInfoChecked, userInfo, autoRegistrationAttempted, registerUser, isVerifyingResume]);

  // Show registration modal if user is not registered (and not in auto-registration mode)
  useEffect(() => {
    if (userInfoChecked && !userInfo) {
      // In Interview Hub mode, only show modal if auto-registration failed
      if (isInterviewHubMode()) {
        setShowRegistrationModal(autoRegistrationAttempted);
      } else {
        setShowRegistrationModal(true);
      }
    } else {
      setShowRegistrationModal(false);
    }
  }, [userInfoChecked, userInfo, autoRegistrationAttempted]);

  useEffect(() => {
    console.log("connection", connection, isConnected);
    if (!connection || !isConnected) {
      return;
    }

    loadJobs();
    fetchAssessmentStatus();
    loadNotifications();
    loadInterviews();
  }, [
    connection,
    isConnected,
    loadJobs,
    fetchAssessmentStatus,
    loadNotifications,
    loadInterviews,
  ]);

  const handleRegister = async (data: {
    email: string;
    first_name: string;
    last_name: string;
  }) => {
    const success = await registerUser(data);
    return success;
  };

  const { clearNetwork } = useAuthStore();

  const handleRetryLogin = () => {
    // Clear all stored data and auth state, then go back to login
    localStorage.removeItem('interview_hub_user_info');
    setVerificationError(null);
    setAutoRegistrationAttempted(false);
    clearNetwork(); // Clear network connection from auth store
    navigate('/');
  };

  // Show verification loading screen
  if (isVerifyingResume) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-indigo-400 to-purple-500 dark:from-blue-800 dark:to-violet-800">
        <div className="max-w-md w-full text-center rounded-2xl p-10 bg-white shadow-2xl dark:bg-gray-800">
          <div className="mb-6">
            <svg
              className="animate-spin h-16 w-16 text-blue-500 mx-auto mb-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">
              Verifying Your Resume
            </h2>
            <p className="text-gray-500 dark:text-gray-400">
              Please wait while we verify your resume...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show verification error screen
  if (verificationError) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-indigo-400 to-purple-500 dark:from-blue-800 dark:to-violet-800">
        <div className="max-w-md w-full text-center rounded-2xl p-10 bg-white shadow-2xl dark:bg-gray-800">
          <div className="mb-6">
            <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-10 h-10 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">
              Resume Verification Failed
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {verificationError}
            </p>
            <button
              onClick={handleRetryLogin}
              className="w-full px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-lg transition-all duration-150 hover:-translate-y-0.5 hover:shadow-lg"
            >
              Try Again with Different Resume
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex h-full w-full bg-white/80 dark:bg-gray-900/80 backdrop-blur rounded-xl overflow-hidden">
        <div className="flex-1 overflow-hidden">
          <Routes>
            <Route index element={<JobListView />} />
            <Route path="jobs/:jobId" element={<JobDetailView />} />
            <Route path="interviewer-hub" element={<InterviewerHubView />} />
            <Route path="*" element={<Navigate to="/interview" replace />} />
          </Routes>
        </div>
        <RightSidebar />
      </div>

      <UserRegistrationModal
        isOpen={showRegistrationModal}
        onRegister={handleRegister}
      />
    </>
  );
};

export default InterviewMainPage;
